import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // Initialize user from sessionStorage AND check for token in localStorage
  const [user, setUser] = useState(() => {
    try {
      // First check if we have a valid token
      const token = localStorage.getItem('token');
      if (!token) {
        return null;
      }
      
      // If we have a token, try to get cached user data
      const savedUser = sessionStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });
  const [permissions, setPermissions] = useState(() => {
    try {
      const savedPerms = sessionStorage.getItem('permissions');
      return savedPerms ? JSON.parse(savedPerms) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const navigate = useNavigate();

  // Helper function to load user permissions
  const loadPermissions = async (userId) => {
    try {
      const response = await api.get(`/api/users/${userId}/permissions`);
      setPermissions(response.data);
      sessionStorage.setItem('permissions', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      console.error('Error loading permissions:', error);
      // If permissions can't be loaded, assume no permissions
      setPermissions({ isAdmin: false, permissions: {} });
      return { isAdmin: false, permissions: {} };
    }
  };

  // Helper function to check if user has a specific permission
  const hasPermission = (module, permission = null) => {
    if (!permissions) {
      console.log('Permissions not loaded yet for module:', module);
      return false;
    }
    
    if (permissions.isAdmin) return true;
    
    const modulePerms = permissions.permissions?.[module];
    if (!modulePerms || modulePerms.length === 0) return false;
    
    if (permission === null) {
      // Just check if module has any permissions
      return true;
    }
    
    // Handle hierarchical inventory permissions
    if (module === 'inventory') {
      // approve can do everything (approve, submit, read)
      if (modulePerms.includes('approve')) return true;
      // submit can do submit and read
      if (permission === 'submit' || permission === 'read_only') {
        return modulePerms.includes('submit') || modulePerms.includes('approve');
      }
      // read_only can only read
      if (permission === 'read_only') {
        return modulePerms.includes('read_only') || modulePerms.includes('submit') || modulePerms.includes('approve');
      }
      // approve permission is exclusive
      return modulePerms.includes(permission);
    }
    
    // For non-inventory modules, check for exact permission or full_access
    return modulePerms.includes(permission) || modulePerms.includes('full_access');
  };

  const checkAuth = async (skipIfChecking = true) => {
    // Prevent multiple simultaneous auth checks
    if (skipIfChecking && isCheckingAuth) {
      console.log('Auth check already in progress, skipping...');
      return false;
    }

    setIsCheckingAuth(true);
    
    try {
      // Check if we have a token first
      const token = localStorage.getItem('token');
      console.log('Checking authentication status...', token ? 'Token present' : 'NO TOKEN');
      
      if (!token) {
        console.log('No token found in localStorage');
        setUser(null);
        return false;
      }
      
      const response = await api.get('/api/check-auth');
      console.log('Auth check response:', response.data);
      
      if (response.data.authenticated && response.data.user) {
        console.log('User is authenticated:', response.data.user);
        setUser(response.data.user);
        sessionStorage.setItem('user', JSON.stringify(response.data.user));
        if (response.data.user.shipEngineAPI) {
          sessionStorage.setItem('shipEngineAPI', response.data.user.shipEngineAPI);
        }
        
        // Load permissions for portal users
        if (response.data.user.isPortalUser) {
          await loadPermissions(response.data.user.id);
        } else {
          // Legacy customer users are admins
          setPermissions({ isAdmin: true, permissions: {} });
        }
        
        return true;
      } else {
        console.log('User is not authenticated, clearing session data');
        setUser(null);
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('shipEngineAPI');
        sessionStorage.removeItem('cart');
        sessionStorage.removeItem('lastNavigationRoute');
        return false;
      }
    } catch (error) {
      console.error('Auth check error:', error);
      
      // Only redirect on certain error types, not all 401s
      if (error.response?.status === 401) {
        const errorData = error.response?.data;
        console.log('401 error details:', errorData);
        
        // Clear invalid token
        localStorage.removeItem('token');
        setUser(null);
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('shipEngineAPI');
        sessionStorage.removeItem('cart');
        sessionStorage.removeItem('lastNavigationRoute');
        sessionStorage.removeItem('permissions');
        
        // Only redirect if we're not already on login
        if (window.location.pathname !== '/login') {
          // Clear any stored navigation routes that might cause conflicts
          sessionStorage.removeItem('lastNavigationRoute');
          
          // Add a small delay to prevent immediate redirect storms
          setTimeout(() => {
            navigate('/login');
          }, 1000);
        }
      } else {
        // For network errors or other issues, don't clear user data immediately
        console.log('Non-401 error, keeping user data temporarily');
      }
      
      return false;
    } finally {
      setLoading(false);
      setIsCheckingAuth(false);
    }
  };

  const verifySession = async (maxAttempts = 3) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        console.log(`Verifying session (attempt ${attempt}/${maxAttempts})...`);
        // Add delay between attempts
        if (attempt > 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        const response = await api.get('/api/check-auth');
        if (response.data.authenticated && response.data.user) {
          console.log('Session verification successful');
          return true;
        }
        console.log('Session verification failed, will retry...');
      } catch (error) {
        console.error('Session verification error:', error);
        if (attempt === maxAttempts) throw error;
      }
    }
    throw new Error('Session verification failed after multiple attempts');
  };

  const login = async (credentials) => {
    try {
      console.log('Attempting login...');
      const response = await api.post('/api/login', credentials);
      console.log('Login response:', response.data);
      
      if (response.data.token && response.data.user) {
        console.log('Login successful, storing JWT token and user data');
        
        // Store JWT token in localStorage
        localStorage.setItem('token', response.data.token);
        
        // Store user data
        setUser(response.data.user);
        sessionStorage.setItem('user', JSON.stringify(response.data.user));
        if (response.data.user.shipEngineAPI) {
          sessionStorage.setItem('shipEngineAPI', response.data.user.shipEngineAPI);
        }

        // Load permissions for portal users
        if (response.data.user.isPortalUser) {
          await loadPermissions(response.data.user.id);
        } else {
          // Legacy customer users are admins
          setPermissions({ isAdmin: true, permissions: {} });
        }

        // Get the last visited route or determine default based on permissions
        const lastRoute = sessionStorage.getItem('lastNavigationRoute');
        let defaultRoute = '/bols';
        
        // Determine default route based on permissions if no last route
        if (!lastRoute && !response.data.user.isAdmin) {
          // Find first available module based on permissions
          if (hasPermission('transportation')) {
            defaultRoute = '/bols';
          } else if (hasPermission('inventory')) {
            defaultRoute = '/inventory';
          } else if (hasPermission('warehouse_receipts')) {
            defaultRoute = '/warehouse-receipts';
          } else if (hasPermission('invoices')) {
            defaultRoute = '/invoices';
          } else if (hasPermission('accounts')) {
            defaultRoute = '/accounts';
          } else if (hasPermission('ext_orders')) {
            defaultRoute = '/ext-orders';
          } else {
            // User has no permissions - show error page or profile
            defaultRoute = '/profile';
          }
        }
        
        const targetRoute = lastRoute || defaultRoute;
        console.log('Token stored, navigating to:', targetRoute);
        navigate(targetRoute);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      setUser(null);
      localStorage.removeItem('token');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('shipEngineAPI');
      sessionStorage.removeItem('lastNavigationRoute');
      throw error;
    }
  };

  const logout = async () => {
    try {
      console.log('Logging out...');
      // Note: No need to call /api/logout with JWT (stateless)
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setPermissions(null);
      localStorage.removeItem('token');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('permissions');
      sessionStorage.removeItem('shipEngineAPI');
      sessionStorage.removeItem('cart');
      sessionStorage.removeItem('lastNavigationRoute');
      navigate('/login');
    }
  };

  // Initial auth check with strict mode protection
  useEffect(() => {
    let mounted = true;
    let timeoutId = null;
    
    const performInitialAuthCheck = async () => {
      if (!mounted) return;
      
      // Prevent duplicate auth checks in React Strict Mode
      const authCheckKey = 'auth-check-' + Date.now();
      const lastAuthCheck = sessionStorage.getItem('lastAuthCheck');
      
      if (lastAuthCheck && (Date.now() - parseInt(lastAuthCheck)) < 1000) {
        console.log('Skipping duplicate auth check due to recent check');
        
        // Even if we skip auth check, ensure permissions are loaded if we have a user
        const savedUser = sessionStorage.getItem('user');
        if (savedUser) {
          try {
            const userData = JSON.parse(savedUser);
            setUser(userData);
            
            // Load permissions if not already loaded
            const savedPerms = sessionStorage.getItem('permissions');
            if (!savedPerms && userData.isPortalUser) {
              console.log('Loading permissions for cached user...');
              await loadPermissions(userData.id);
            } else if (savedPerms) {
              setPermissions(JSON.parse(savedPerms));
            } else if (!userData.isPortalUser) {
              // Legacy customer users are admins
              setPermissions({ isAdmin: true, permissions: {} });
            }
          } catch (error) {
            console.error('Error loading cached user data:', error);
          }
        }
        
        setLoading(false);
        return;
      }
      
      sessionStorage.setItem('lastAuthCheck', Date.now().toString());
      
      console.log('AuthContext mounted, checking auth...');
      
      // Small delay to batch multiple rapid calls
      timeoutId = setTimeout(async () => {
        if (mounted) {
          await checkAuth(false); // Don't skip if checking on initial mount
        }
      }, 150);
    };
    
    performInitialAuthCheck();
    
    return () => {
      mounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  // Handle navigation based on auth state
  useEffect(() => {
    if (loading) return; // Wait for initial auth check

    const currentPath = window.location.pathname;
    console.log('Auth state changed:', { user: !!user, currentPath, loading });

    // Only redirect to login if not authenticated AND auth check is complete AND not already on login
    if (!user && currentPath !== '/login' && !loading) {
      console.log('No user found after auth check complete, redirecting to login');
      // Clear any stored routes that might cause confusion
      sessionStorage.removeItem('lastNavigationRoute');
      navigate('/login');
    } else if (user && (currentPath === '/' || currentPath.match(/^\/[^\/]+\/?$/))) {
      // If user is authenticated and on root path, redirect to last route or BOLs
      const lastRoute = sessionStorage.getItem('lastNavigationRoute');
      
      // List of valid application routes
      const validRoutes = ['/bols', '/inventory', '/orders', '/ext-orders', '/accounts', '/invoices', '/profile', '/catalog', '/warehouse-receipts', '/rate-calculator', '/users', '/pending-approvals'];
      
      // Don't redirect if we're already on a valid route
      if (validRoutes.includes(currentPath)) {
        return;
      }
      
      const targetRoute = (lastRoute && validRoutes.includes(lastRoute)) ? lastRoute : '/bols';
      
      console.log('User authenticated on root path, redirecting to:', targetRoute);
      navigate(targetRoute);
    }
  }, [user, loading, navigate]);

  return (
    <AuthContext.Provider value={{ user, permissions, loading, isCheckingAuth, login, logout, checkAuth, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
